import React, { useEffect, useState } from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { getGuestIconUrl } from '../../../services/guestRoomIcons';
import MonitorCartInspectorFooterRow from './MonitorCartInspectorFooterRow';
import MiniProgressBar from '../../components/Mode/taskProcess/MiniProgressBar';
import {
    BADGE_DISPLAY_CONFIRMATION,
    getMonitorBadgeLabel,
    MONITOR_BADGE_DAY_USE_GRADIENT_BG
} from '../../../services/monitorBadgeDisplay';
import {
    buildMonitorDoorCardLayoutState,
    getInitials,
    hasMonitorProgressTiming,
    getReservationHeaderPresentation,
    MONITOR_DOOR_CARD_V2_WIDTH_PX,
    MONITOR_BADGE_HEADER_HEIGHT_PX,
    MONITOR_DOOR_FOOTER_VARIANT_ACCENT,
    MONITOR_DOOR_FOOTER_VARIANT_LIGHT,
    MONITOR_DOOR_FOOTER_VARIANT_SOLID,
    resolveMonitorDoorCardFooterStyle,
    splitFooterLabelLines
} from './monitorDoorCardModel';
import MonitorDoorIcon from './MonitorDoorIcon';

/**
 * Redesigned monitor door card (rounded shell, integrated reservation header, clean status footer).
 */
const MonitorDoorCardV2 = ({
    data,
    roomNumber,
    status,
    statusColor,
    isDnd = false,
    isProcessMode = false,
    room_statuses = null,
    show_icon = 0,
    icon_blink_interval = null,
    isMonitor = false,
    onConfirmationClick = null,
    badgeDisplayMode = BADGE_DISPLAY_CONFIRMATION,
    mbb = 'my-3',
    compactDoorEmbed = false,
    badgeSlotRowCount = null,
    footerSlotHeightPx = null
}) => {
    const [blink, setBlink] = useState(true);
    const [doorBlink, setDoorBlink] = useState(true);

    useEffect(() => {
        if (isMonitor && icon_blink_interval > 0) {
            setBlink(true);
            const interval = setInterval(() => setBlink((prev) => !prev), icon_blink_interval);
            return () => clearInterval(interval);
        }
        setBlink(true);
        return undefined;
    }, [isMonitor, icon_blink_interval]);

    useEffect(() => {
        if ((isMonitor || isProcessMode) && data?.door_panel_blink_interval > 0) {
            setDoorBlink(true);
            const interval = setInterval(
                () => setDoorBlink((prev) => !prev),
                data.door_panel_blink_interval
            );
            return () => clearInterval(interval);
        }
        setDoorBlink(true);
        return undefined;
    }, [isMonitor, isProcessMode, data?.door_panel_blink_interval]);

    const layout = buildMonitorDoorCardLayoutState({
        data,
        isDnd,
        status,
        show_icon,
        isMonitor,
        badgeDisplayMode
    });

    const calculateTimeDisplay = () => {
        if (!hasMonitorProgressTiming(data)) return null;
        const now = Date.now();
        const startTime = new Date(data.time_start);
        const elapsed = Math.floor((now - startTime.getTime()) / 1000);
        const totalSeconds = data.function_seconds_allocated;
        const formatTime = (seconds) => {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const remainingSeconds = seconds % 60;
            if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
            }
            return `${minutes.toString().padStart(2, '0')}.${remainingSeconds.toString().padStart(2, '0')}`;
        };
        return `${formatTime(elapsed)}/${formatTime(totalSeconds)}`;
    };

    const renderTooltip = (props) => (
        <Tooltip
            id="room-tooltip-v2"
            {...props}
            style={{
                backgroundColor: '#333',
                color: '#fff',
                borderRadius: '8px',
                padding: '10px 15px',
                fontSize: '14px',
                textAlign: 'center',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                ...props.style
            }}
        >
            <div className="d-flex flex-column align-items-center gap-1">
                <div
                    style={{
                        backgroundColor: '#06A9F4',
                        color: '#fff',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: 16,
                        fontWeight: 'bold',
                        marginBottom: 8
                    }}
                >
                    {getInitials(data?.status_display_text || status)}
                </div>
                <div>
                    <strong>Room Type:</strong> {data?.room_type_name || 'N/A'}
                </div>
                <div>
                    <strong>Status:</strong> {data?.status_display_text || status || 'N/A'}
                </div>
                <div>
                    <strong>Floor:</strong> {data?.floor || 'N/A'}
                </div>
                {hasMonitorProgressTiming(data) && (
                    <div>
                        <strong>{calculateTimeDisplay() || 'N/A'}</strong>
                    </div>
                )}
            </div>
        </Tooltip>
    );

    if (status === null || !status) {
        return null;
    }

    const isDndRoom = isDnd || status?.toLowerCase?.() === 'dnd';
    const doorPanelOpacity =
        data?.door_panel_blink_interval > 0 ? (doorBlink ? 1 : 0) : 1;

    const renderReservationHeaderBand = (key, onClick, title, content, style = {}) => (
        <button
            key={key}
            type="button"
            className="monitor-door-card-v2__res-header p-0 border-0 w-100"
            onClick={onClick}
            title={title}
            style={style}
        >
            {content}
        </button>
    );

    const reservationHeaders = () => {
        if (layout.dayUsePlan) {
            const plan = layout.dayUsePlan;
            return renderReservationHeaderBand(
                'day-use',
                () => onConfirmationClick?.(plan.confirmationForClick, 'show'),
                plan.tooltip,
                <span className="monitor-door-card-v2__res-header-label">{plan.display}</span>,
                { background: MONITOR_BADGE_DAY_USE_GRADIENT_BG }
            );
        }

        if (!layout.confirmationRows.length) return null;

        return layout.confirmationRows.map((conf, index) => {
            const { display, tooltip, confirmationForClick } = getMonitorBadgeLabel(
                conf,
                badgeDisplayMode
            );
            const pres = getReservationHeaderPresentation(conf);
            const label = String(display || '').toUpperCase();

            return renderReservationHeaderBand(
                confirmationForClick || index,
                () => onConfirmationClick?.(confirmationForClick, 'show'),
                tooltip,
                <span className="monitor-door-card-v2__res-header-label d-inline-flex align-items-center justify-content-center gap-1">
                    {pres.showCaret && (
                        <span className="monitor-door-card-v2__caret" aria-hidden>
                            {pres.caret === 'up' ? '▲' : '▼'}
                        </span>
                    )}
                    {pres.showDot && (
                        <span className="monitor-door-card-v2__header-dot" aria-hidden />
                    )}
                    <span>{label}</span>
                </span>,
                { background: pres.background }
            );
        });
    };

    const renderStatusFooter = () => {
        if (layout.isOutOfService) {
            const serviceStatusText =
                data?.service_status === 'out_of_service' ? 'OUT OF SERVICE' : 'OUT OF ORDER';
            const serviceLines = splitFooterLabelLines(serviceStatusText);
            return (
                <div
                    className="monitor-door-card-v2__footer monitor-door-card-v2__footer--service"
                    style={{ opacity: doorPanelOpacity, transition: 'opacity 0.2s' }}
                >
                    {serviceLines.map((line) => (
                        <span key={line} className="monitor-door-card-v2__footer-text">
                            {line}
                        </span>
                    ))}
                </div>
            );
        }

        if (data?.status_display_text) {
            const footerStyle = resolveMonitorDoorCardFooterStyle(data);
            const footerVariantByKind = {
                [MONITOR_DOOR_FOOTER_VARIANT_ACCENT]: 'monitor-door-card-v2__footer--accent',
                [MONITOR_DOOR_FOOTER_VARIANT_LIGHT]: 'monitor-door-card-v2__footer--light',
                [MONITOR_DOOR_FOOTER_VARIANT_SOLID]: 'monitor-door-card-v2__footer--solid'
            };
            const footerVariantClass =
                footerVariantByKind[footerStyle.variant] ?? 'monitor-door-card-v2__footer--solid';

            return (
                <div
                    className={`monitor-door-card-v2__footer ${footerVariantClass}`}
                    style={{
                        backgroundColor: footerStyle.backgroundColor,
                        color: footerStyle.color,
                        borderTop: footerStyle.borderTop,
                        opacity: doorPanelOpacity,
                        transition: 'opacity 0.2s'
                    }}
                >
                    {footerStyle.textLines.length > 0 ? (
                        footerStyle.textLines.map((line) => (
                            <span key={line} className="monitor-door-card-v2__footer-text">
                                {line}
                            </span>
                        ))
                    ) : (
                        <span className="monitor-door-card-v2__footer-text">
                            {data.status_display_text}
                        </span>
                    )}
                </div>
            );
        }

        if (isProcessMode && room_statuses?.status_display_text) {
            const processStatusData = {
                status_display_text: room_statuses.status_display_text,
                status_display_backcolor: room_statuses.status_display_backcolor,
                status_display_forecolor: room_statuses.status_display_forecolor,
                status_display_inner_border_color: room_statuses.status_display_inner_border_color,
                status_display_outer_border_color: room_statuses.status_display_outer_border_color
            };
            const footerStyle = resolveMonitorDoorCardFooterStyle(processStatusData);
            const footerVariantByKind = {
                [MONITOR_DOOR_FOOTER_VARIANT_ACCENT]: 'monitor-door-card-v2__footer--accent',
                [MONITOR_DOOR_FOOTER_VARIANT_LIGHT]: 'monitor-door-card-v2__footer--light',
                [MONITOR_DOOR_FOOTER_VARIANT_SOLID]: 'monitor-door-card-v2__footer--solid'
            };
            const footerVariantClass =
                footerVariantByKind[footerStyle.variant] ?? 'monitor-door-card-v2__footer--solid';

            return (
                <div
                    className={`monitor-door-card-v2__footer ${footerVariantClass}`}
                    style={{
                        backgroundColor: footerStyle.backgroundColor,
                        color: footerStyle.color,
                        borderTop: footerStyle.borderTop,
                        opacity: doorPanelOpacity,
                        transition: 'opacity 0.2s'
                    }}
                >
                    {footerStyle.textLines.map((line) => (
                        <span key={line} className="monitor-door-card-v2__footer-text">
                            {line}
                        </span>
                    ))}
                </div>
            );
        }

        if (data?.status_display_icon) {
            return (
                <div className="monitor-door-card-v2__footer monitor-door-card-v2__footer--icon p-0">
                    <img src={data.status_display_icon} alt="" className="monitor-door-card-v2__status-img" />
                </div>
            );
        }

        return (
            <div
                className="monitor-door-card-v2__footer"
                style={{
                    backgroundColor: statusColor || '#6c757d',
                    color: '#fff',
                    opacity: doorPanelOpacity,
                    transition: 'opacity 0.2s'
                }}
            >
                <span className="monitor-door-card-v2__footer-text">{status}</span>
            </div>
        );
    };

    const hasReservationBadges = layout.headerRowCount > 0;
    const effectiveBadgeSlotRows =
        badgeSlotRowCount != null ? badgeSlotRowCount : layout.headerRowCount;
    const badgeSlotHeightPx = effectiveBadgeSlotRows * MONITOR_BADGE_HEADER_HEIGHT_PX;
    const effectiveFooterSlotPx =
        footerSlotHeightPx != null ? footerSlotHeightPx : layout.belowBlockHeightPx;
    const showBelowBlock =
        !isProcessMode &&
        (layout.belowBlockHeightPx > 0 ||
            layout.showMonitorTaskProgressBar ||
            layout.monitorHasBottomFooter ||
            Boolean(data?.label_1) ||
            effectiveFooterSlotPx > 0);

    const cardShell = (
        <div
            className={[
                'monitor-door-card-v2',
                'position-relative',
                isProcessMode ? 'monitor-door-card-v2--process noBottomMargin' : null,
                hasReservationBadges ? 'monitor-door-card-v2--has-badge' : null,
                compactDoorEmbed ? 'monitor-door-card-v2--embed mb-0' : mbb
            ]
                .filter(Boolean)
                .join(' ')}
            style={{
                width: isProcessMode ? undefined : MONITOR_DOOR_CARD_V2_WIDTH_PX,
                maxWidth: '100%',
                paddingTop: badgeSlotHeightPx,
                paddingBottom: compactDoorEmbed || isProcessMode ? 0 : layout.shellMarginBottom,
                boxSizing: 'border-box'
            }}
        >
            {badgeSlotHeightPx > 0 ? (
                <div
                    className="monitor-door-card-v2__badge-slot"
                    style={{ height: badgeSlotHeightPx }}
                >
                    {hasReservationBadges ? (
                        <div className="monitor-door-card-v2__badge-stack">{reservationHeaders()}</div>
                    ) : null}
                </div>
            ) : null}

            <div className="monitor-door-card-v2__shell d-flex flex-column">
                <div className="monitor-door-card-v2__body flex-grow-1 d-flex flex-column align-items-center">
                    <h5 className="monitor-door-card-v2__room-num mb-0">{roomNumber}</h5>
                    <div className="monitor-door-card-v2__door-wrap">
                        <MonitorDoorIcon />
                        {layout.isOccupied && (
                            <img
                                src={getGuestIconUrl(data?.is_vip)}
                                alt=""
                                className="monitor-door-card-v2__guest-icon"
                            />
                        )}
                    </div>

                    {isDndRoom && (
                        <div className="monitor-door-card-v2__dnd mt-1">
                            <div className="dnd-hanger position-static" style={{ transform: 'scale(0.72)' }}>
                                <div className="hanger-hole" />
                                <div className="hanger-body">
                                    <span className="dnd-text">DND</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {renderStatusFooter()}
            </div>

            {showBelowBlock ? (
                <div
                    className="monitor-door-card-v2__below d-flex flex-column"
                    style={
                        effectiveFooterSlotPx > 0
                            ? { minHeight: effectiveFooterSlotPx }
                            : undefined
                    }
                >
                    {layout.showMonitorTaskProgressBar && (
                        <div className="monitor-door-card-v2__progress px-1">
                            <MiniProgressBar
                                timeStart={layout.progressTimingData.time_start}
                                functionSecondsAllocated={Number(
                                    layout.progressTimingData.function_seconds_allocated
                                )}
                                isDark
                                top={0}
                            />
                        </div>
                    )}

                    <MonitorCartInspectorFooterRow
                        layout={layout}
                        icon_blink_interval={icon_blink_interval}
                        blink={blink}
                    />

                    {data?.label_1 && (
                        <div className="monitor-door-card-v2__label-row">
                            <span className="text-black fs-8 fw-medium text-uppercase text-center pt-1 w-100 d-block">
                                {data.label_1}
                            </span>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );

    if (compactDoorEmbed || isProcessMode) {
        return cardShell;
    }

    return (
        <OverlayTrigger placement="top" overlay={renderTooltip} delay={{ show: 250, hide: 400 }}>
            <div className="monitor-door-card-v2-slot flex-shrink-0">{cardShell}</div>
        </OverlayTrigger>
    );
};

export default MonitorDoorCardV2;
